import { type AssetManifest, MAP_CATALOG } from '@kenocyber/shared';
import Phaser from 'phaser';
import { assetUrl, findAnimatedAsset, findStaticAsset } from './assets';

/**
 * **O Drone e o Leviatã, desenhados uma vez para as duas arenas** (V28I.P).
 *
 * ## Porque isto saiu de dentro do duelo
 *
 * Porque passou a ser preciso em dois sítios. As duas invocações existiam só na
 * arena, e a cena do duelo era a única que sabia desenhá-las; o mundo aberto
 * passou a tê-las, e uma segunda cópia de duzentas linhas de arte seria a
 * segunda oportunidade de as duas divergirem — um Leviatã com hélice num sítio
 * e sem ela no outro, ou um Drone que só é vermelho num dos modos.
 *
 * O que ficou de cada lado é o que é da ARENA: a conversão para unidades de
 * mundo, a profundidade, o diâmetro do casco de quem invocou, e a cor do lado.
 * O que vive aqui é a FORMA, que não pertence a nenhum modo.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não decide nada. Ele não sabe o que é dano, alcance, vida, tempo restante nem
 * quem pode entrar: recebe uma lista autoritativa e desenha-a. A estrutura
 * aparece porque a sala a publicou, e desaparece porque a sala deixou de o
 * fazer.
 *
 * ## A arte é a APROVADA, e não é nova
 *
 * - o **Drone** usa provisoriamente o sprite do chefe do mapa 1, decisão do
 *   dono, e desenha uma forma visível enquanto o ficheiro não chega;
 * - o **Leviatã** é o corpo PNG aprovado, **sem hélice gravada**, com o rotor
 *   como imagem separada a girar, seis canhões desenhados na chapa, entradas
 *   laterais, o néon aditivo dos vermelhos, e as setas que piscam enquanto
 *   ninguém entrou. A escala vem da vida de quem o criou e tem um mínimo
 *   grande.
 *
 * Nada disto é decidido aqui: são os mesmos números, movidos.
 */

/* -------------------------------------------------------------------------- */
/*  As texturas e os números da arte                                          */
/* -------------------------------------------------------------------------- */

/** O sprite emprestado do chefe do mapa 1. Provisório, por decisão do dono. */
export const SUMMON_DRONE_TEXTURE = 'pvp-summon-drone';

/** A chapa do canhoneiro e a hélice. Duas imagens, porque só uma gira. */
export const SUMMON_LEVIATHAN_BODY_TEXTURE = 'pvp-summon-leviathan-body';
export const SUMMON_LEVIATHAN_ROTOR_TEXTURE = 'pvp-summon-leviathan-rotor';

const SUMMON_LEVIATHAN_BODY_ASSET = 'leviathan_body';
const SUMMON_LEVIATHAN_ROTOR_ASSET = 'leviathan_rotor';

/** Quantas voltas por segundo a hélice dá. */
export const SUMMON_LEVIATHAN_SPIN_PER_SECOND = 7;

/** O vermelho néon dos seis canhões, e da salva que sai deles. */
export const SUMMON_LEVIATHAN_SHOT_COLOUR = 0xff_23_18;

/** A proporção da chapa, tal como o pipeline a fotografou. */
export const SUMMON_LEVIATHAN_ART_RATIO = 1024 / 1536;

/** Quanto do diâmetro a hélice ocupa. */
export const SUMMON_LEVIATHAN_ROTOR_SPAN = 0.78;

/** A chapa é fotografada de nariz para cima; a arena mede o rumo para a direita. */
export const SUMMON_LEVIATHAN_ART_TURN = Math.PI / 2;

/* -------------------------------------------------------------------------- */
/*  Os pedidos de textura                                                     */
/* -------------------------------------------------------------------------- */

/**
 * **Pede a folha do chefe do mapa 1, uma vez.**
 *
 * Uma folha 3x3 e não uma imagem: é o mesmo ficheiro que a caça carrega, e
 * carregá-lo como imagem daria um drone com os nove quadros lado a lado.
 *
 * Sem a arte, a forma continua a ser desenhada. Uma cena que rebentasse por
 * causa de um ficheiro em falta seria uma partida perdida.
 */
export function requestDroneTexture(
  scene: Phaser.Scene,
  manifest: AssetManifest,
  pending: Set<string>,
): void {
  if (scene.textures.exists(SUMMON_DRONE_TEXTURE) || pending.has(SUMMON_DRONE_TEXTURE)) {
    return;
  }
  const map = MAP_CATALOG[0];
  if (!map?.bossAssetId) {
    return;
  }
  try {
    const boss = findAnimatedAsset(manifest, map.bossAssetId);
    pending.add(SUMMON_DRONE_TEXTURE);
    scene.load.spritesheet(SUMMON_DRONE_TEXTURE, assetUrl(manifest, boss), {
      frameWidth: boss.layout.frameWidth,
      frameHeight: boss.layout.frameHeight,
    });
    scene.load.once(`filecomplete-spritesheet-${SUMMON_DRONE_TEXTURE}`, () => {
      pending.delete(SUMMON_DRONE_TEXTURE);
    });
    /*
     * A mesma rede de `requestTexture`: se o ficheiro falhar, o evento acima
     * nunca chega e a chave ficaria pendente para sempre — e a forma nunca
     * seria substituída nem re-pedida.
     */
    scene.load.once(Phaser.Loader.Events.COMPLETE, () => pending.delete(SUMMON_DRONE_TEXTURE));
    scene.load.start();
  } catch {
    pending.delete(SUMMON_DRONE_TEXTURE);
  }
}

/** A chapa e a hélice. O mesmo par que o casco e o canhão usam. */
export function requestLeviathanTexture(
  scene: Phaser.Scene,
  manifest: AssetManifest,
  pending: Set<string>,
): void {
  for (const [key, id] of [
    [SUMMON_LEVIATHAN_BODY_TEXTURE, SUMMON_LEVIATHAN_BODY_ASSET],
    [SUMMON_LEVIATHAN_ROTOR_TEXTURE, SUMMON_LEVIATHAN_ROTOR_ASSET],
  ] as const) {
    if (scene.textures.exists(key) || pending.has(key)) {
      continue;
    }
    try {
      const asset = findStaticAsset(manifest, id);
      pending.add(key);
      scene.load.image(key, assetUrl(manifest, asset));
      scene.load.once(`filecomplete-image-${key}`, () => {
        pending.delete(key);
      });
      scene.load.once(Phaser.Loader.Events.COMPLETE, () => pending.delete(key));
      scene.load.start();
    } catch {
      pending.delete(key);
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  O corpo                                                                   */
/* -------------------------------------------------------------------------- */

/** Como este corpo foi construído: com a arte, ou com a forma de reserva. */
export type SummonBuiltAs = 'sprite' | 'shape';

/** Se a arte deste tipo já chegou. Decide se o corpo é refeito quando ela chegar. */
export function summonBuiltAs(scene: Phaser.Scene, kind: 'd' | 'l'): SummonBuiltAs {
  const art = kind === 'd' ? SUMMON_DRONE_TEXTURE : SUMMON_LEVIATHAN_BODY_TEXTURE;
  return scene.textures.exists(art) ? 'sprite' : 'shape';
}

/**
 * **O corpo de uma invocação**: a arte, a direção e uma barra de vida.
 *
 * As mesmas expressões, os mesmos números e a mesma ordem de construção que o
 * duelo sempre teve. As setas de embarque são um contentor com nome, para quem
 * sincroniza as poder acender e apagar sem saber como elas são feitas.
 */
export function buildSummonBody(
  scene: Phaser.Scene,
  input: {
    readonly kind: 'd' | 'l';
    readonly diameter: number;
    /** A cor do lado de quem a invocou. */
    readonly tint: number;
    /** A profundidade de um casco nesta cena. A estrutura acomoda-se a ela. */
    readonly hullDepth: number;
    /** E a dos canhões, que é onde uma estrutura AÉREA fica: por cima. */
    readonly cannonDepth: number;
  },
): Phaser.GameObjects.Container {
  const { kind, diameter, tint } = input;
  const parts: Phaser.GameObjects.GameObject[] = [];
  if (kind === 'd' && scene.textures.exists(SUMMON_DRONE_TEXTURE)) {
    /*
     * O primeiro quadro da folha do chefe, escalado ao diâmetro que a sala
     * decidiu. A folha tem nove quadros; um drone não é o chefe a animar, é o
     * corpo dele emprestado — a decisão do dono foi o SPRITE, e não a animação.
     */
    const sprite = scene.add.sprite(0, 0, SUMMON_DRONE_TEXTURE, 0);
    sprite.setDisplaySize(diameter, diameter);
    parts.push(sprite);
  } else if (kind === 'd') {
    /*
     * **A forma de reserva, e ela tem de SER VISTA.**
     *
     * Ela era um disco quase preto sobre um fundo quase preto, e o dono viu o
     * drone disparar sem ver o drone. Agora o corpo é pintado NA COR do lado,
     * com um halo aditivo por baixo e um contorno claro por cima.
     */
    parts.push(
      scene.add.circle(0, 0, diameter * 0.62, tint, 0.22).setBlendMode(Phaser.BlendModes.ADD),
    );
    parts.push(scene.add.circle(0, 0, diameter / 2, tint, 0.75).setStrokeStyle(3, 0xffffff, 0.9));
    parts.push(
      scene.add.triangle(
        diameter * 0.45,
        0,
        0,
        -diameter * 0.18,
        diameter * 0.3,
        0,
        0,
        diameter * 0.18,
        tint,
        0.95,
      ),
    );
  } else if (scene.textures.exists(SUMMON_LEVIATHAN_BODY_TEXTURE)) {
    /*
     * **Só o modelo, e mais nada** (decisão do dono).
     *
     * Os canos, as bocas, as portas e as setas estiveram desenhados por cima da
     * chapa e foram mandados embora: cada um era um lembrete de uma coisa que o
     * próprio modelo já mostra.
     */
    const heli = scene.add.image(0, 0, SUMMON_LEVIATHAN_BODY_TEXTURE);
    heli.setDisplaySize(diameter * SUMMON_LEVIATHAN_ART_RATIO, diameter);
    heli.setRotation(SUMMON_LEVIATHAN_ART_TURN);
    parts.push(heli);
    /*
     * **O NÉON dos vermelhos da chapa.**
     *
     * Uma SEGUNDA cópia da mesma imagem, somada por cima da primeira: preto
     * somado é zero, portanto a blindagem não muda e as únicas partes que
     * acrescentam luz são as que já brilham — as janelas, as juntas e os anéis
     * dos canhões.
     */
    const neon = scene.add.image(0, 0, SUMMON_LEVIATHAN_BODY_TEXTURE);
    neon.setDisplaySize(diameter * SUMMON_LEVIATHAN_ART_RATIO, diameter);
    neon.setRotation(SUMMON_LEVIATHAN_ART_TURN);
    neon.setTint(SUMMON_LEVIATHAN_SHOT_COLOUR);
    neon.setBlendMode(Phaser.BlendModes.ADD);
    neon.setAlpha(0.7);
    neon.setName('neon');
    parts.push(neon);
    /*
     * **A HÉLICE, por cima da chapa e a girar.**
     *
     * A arte do canhoneiro vem sem rotor, de propósito: um rotor pintado nela
     * estaria parado. Desenhada em NORMAL e não somada, porque ela é uma peça
     * sólida que tapa o que está por baixo, e não uma luz.
     */
    if (scene.textures.exists(SUMMON_LEVIATHAN_ROTOR_TEXTURE)) {
      const blades = scene.add.image(0, 0, SUMMON_LEVIATHAN_ROTOR_TEXTURE);
      const span = diameter * SUMMON_LEVIATHAN_ROTOR_SPAN;
      blades.setDisplaySize(span, span);
      blades.setName('rotor');
      parts.push(blades);
    }
  } else {
    /* Enquanto a chapa não chega: um corpo, uma cabina e um rotor. */
    parts.push(
      scene.add
        .rectangle(0, 0, diameter, diameter * 0.55, 0x0d1622, 0.96)
        .setStrokeStyle(3, tint, 0.95),
    );
    parts.push(
      scene.add
        .rectangle(diameter * 0.24, 0, diameter * 0.26, diameter * 0.3, tint, 0.18)
        .setStrokeStyle(2, tint, 0.7),
    );
    parts.push(
      scene.add
        .rectangle(0, 0, Math.max(3, diameter * 0.035), diameter * 1.15, tint, 0.55)
        .setBlendMode(Phaser.BlendModes.ADD),
    );
    parts.push(scene.add.circle(0, 0, Math.max(3, diameter * 0.05), tint, 0.9));
  }
  /* A barra, por cima e ancorada à esquerda: ela encolhe para um lado só. */
  const bar = scene.add
    .rectangle(
      -diameter / 2,
      -diameter * 0.5,
      diameter,
      Math.max(3, diameter * 0.07),
      0xff5a5a,
      0.9,
    )
    .setOrigin(0, 0.5);
  parts.push(bar);
  /*
   * **Uma estrutura aérea passa POR CIMA das naves** — "o helicóptero deve
   * sobrepor qualquer nave", decisão do dono, e é também o que a física sugere:
   * ele voa, e elas passam por baixo. Um Drone continua debaixo delas: ele é do
   * tamanho de meia nave.
   */
  return scene.add
    .container(0, 0, parts)
    .setDepth(kind === 'l' ? input.cannonDepth + 1 : input.hullDepth - 1);
}

/**
 * **A barra de vida de um corpo já construído**, em basis points.
 *
 * A última peça do contentor, sempre: quem constrói põe-na no fim, e quem
 * sincroniza lê-a de lá. Uma procura por nome seria um segundo contrato.
 */
export function setSummonHealth(container: Phaser.GameObjects.Container, healthBps: number): void {
  const bar = container.getAt(container.length - 1) as Phaser.GameObjects.Rectangle;
  bar.setScale(Math.max(0, healthBps) / 10_000, 1);
}

/**
 * **A hélice gira, e só ela.**
 *
 * O ângulo vem do relógio da CENA e não de um acumulador: é uma função do
 * tempo, portanto não deriva, não precisa de estado por invocação, e uma aba
 * escondida não a faz acelerar quando volta.
 */
export function spinSummonRotor(container: Phaser.GameObjects.Container, nowMs: number): void {
  const blades = container.getByName('rotor') as Phaser.GameObjects.Image | null;
  blades?.setRotation((nowMs / 1_000) * SUMMON_LEVIATHAN_SPIN_PER_SECOND * Math.PI * 2);
}

/**
 * **As setas de embarque piscam ENQUANTO ninguém entrou.**
 *
 * A ausência de piloto é a estrutura à espera. Um contentor que não tenha setas
 * — a chapa aprovada não tem — simplesmente não faz nada aqui.
 */
export function setSummonBoardingArrows(
  container: Phaser.GameObjects.Container,
  waiting: boolean,
  nowMs: number,
): void {
  const arrows = container.getByName('arrows') as Phaser.GameObjects.Container | null;
  if (arrows === null) {
    return;
  }
  arrows.setVisible(waiting);
  if (waiting) {
    arrows.setAlpha(0.4 + 0.4 * Math.sin(nowMs / 170));
  }
}
